import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { portalRequest } from '../../utils/portalApi'
import { ArrowLeft, Trophy, Save } from 'lucide-react'

const sportTypes = [
  'Soccer', 'Basketball', 'Baseball', 'Softball', 'Football', 
  'Volleyball', 'Tennis', 'Swimming', 'Track & Field', 'Lacrosse',
  'Hockey', 'Wrestling', 'Gymnastics', 'Cheerleading', 'Golf', 'Other'
]

export default function PortalCreateTeam() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    name: '',
    sport: '',
    age_group: '',
    season: '',
    description: '',
  })

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const payload = { ...form }
    Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k] })

    try {
      const data = await portalRequest('/api/teams', {
        method: 'POST',
        body: JSON.stringify(payload)
      })
      const teamId = data.team_id || data._id
      navigate(`/portal/team/${teamId}`)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-zinc-900 px-4 py-8">
      <div className="max-w-lg mx-auto space-y-6">
        <button
          onClick={() => navigate('/portal')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back</span>
        </button>

        <div className="text-center">
          <Trophy className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-white">Create a Team</h1>
          <p className="text-zinc-400 mt-1">Set up a new team and invite your members</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-500/10 text-red-400 px-4 py-3 rounded-xl text-sm">{error}</div>
          )}

          <div className="bg-zinc-800 rounded-2xl border border-zinc-700 p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">Team Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-green-500 outline-none"
                placeholder="e.g., Thunder FC U14"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">Sport *</label>
              <select
                value={form.sport}
                onChange={(e) => update('sport', e.target.value)}
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white focus:ring-2 focus:ring-green-500 outline-none"
                required
              >
                <option value="">Select sport...</option>
                {sportTypes.map(s => <option key={s} value={s.toLowerCase()}>{s}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1.5">Age Group</label>
                <input
                  type="text"
                  value={form.age_group}
                  onChange={(e) => update('age_group', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-green-500 outline-none"
                  placeholder="e.g., U14, Varsity"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1.5">Season</label>
                <input
                  type="text"
                  value={form.season}
                  onChange={(e) => update('season', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-green-500 outline-none"
                  placeholder="e.g., Spring 2026"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-green-500 outline-none resize-none"
                placeholder="Brief description of the team..."
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-4 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 disabled:opacity-50 transition-all shadow-lg shadow-green-500/20"
          >
            {loading ? (
              <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
            ) : (
              <Save className="w-5 h-5" />
            )}
            Create Team
          </button>
        </form>
      </div>
    </div>
  )
}
